import { Box } from '@mui/material';
import { IconButton } from '@sweep-io/sweep-design';
import { Close } from '@sweep-io/sweep-design/dist/icons';
import { useCallback, useEffect } from 'react';
import { useAiAgentApiFacade } from '../../../../../apis/facades/useAiAgentApiFacade';
import { telemetry } from '../../../../../telemetry';
import { ProcessMiningPrompt } from './ProcessMiningPrompt';
import { AiChatMessage } from '../../../../common/ai-chat/aiChatTypes';
import { ChatCompletionRequestMessageRole } from '@server/ai';
import { usePageHelpers } from '../usePageHelpers';
import { DateTime } from 'luxon';
import { useProcessMining } from '../useProcessMining';
import { setIsPageLoading } from '../../../../../reducers/canvasPageStateReducer';
import { useDispatch } from 'react-redux';
import { useFunnelMapApiFacade } from '../../../../../apis/facades/funnel-map/useFunnelMapApiFacade';
import { setInitialFunnelMap } from '../../../../../reducers/united-canvas/unitedCanvasReducer';
import { normalizeFunnelMapPositions } from '../../../funnel-map-flow/normalizeFunnelMapPositions';
import { useAutoSaveContext } from '../../../funnel-map-flow/auto-save/AutoSaveContext';
import { ProcessMiningLoader } from './ProcessMiningLoader';
import useSendBiEvent from '../../../../../hooks/useSendBiEvent';
import { AI_AGENTS_EVENTS } from '../../../../../services/events';

export const ProcessMiningPanel = ({ onClose }: { onClose?: () => void }) => {
  const { post_processMining } = useAiAgentApiFacade();
  const { funnelMapId } = usePageHelpers();
  const { get_funnelMap } = useFunnelMapApiFacade();
  const dispatch = useDispatch();
  const { funnelMapVersion } = useAutoSaveContext();
  const sendBiEvent = useSendBiEvent();

  const { isLoading, isCompleted, error, setLoading, setError, message } = useProcessMining(
    funnelMapId || '',
  );

  const onComplete = useCallback(async () => {
    if (isCompleted && !!funnelMapId) {
      try {
        const funnelMap = (await get_funnelMap(funnelMapId, true)) as FunnelMap;
        funnelMapVersion.current = funnelMap.version;
        dispatch(setInitialFunnelMap(normalizeFunnelMapPositions(funnelMap)));
        dispatch(setIsPageLoading({ isPageLoading: false }));
      } catch (error) {
        telemetry.captureError(error);
      }
    }
  }, [dispatch, funnelMapId, get_funnelMap, isCompleted, funnelMapVersion]);

  useEffect(() => {
    if (isCompleted) {
      onComplete();
    }
  }, [isCompleted, onComplete]);

  const onSendMessage = useCallback(
    async (messageContent: string) => {
      if (!funnelMapId) {
        return;
      }
      sendBiEvent({ name: AI_AGENTS_EVENTS.generateProcessMiningFromCanvas });

      const message: AiChatMessage = {
        role: ChatCompletionRequestMessageRole.USER,
        content: messageContent,
        createdAt: DateTime.now().toISO(),
      };

      try {
        setLoading(true);
        // Response will come via websocket
        await post_processMining(funnelMapId, { message });
      } catch (error) {
        telemetry.captureError(error);
        setError(`Couldn't generate results. Try again with a new prompt.`);
      }
    },
    [funnelMapId, sendBiEvent, setLoading, post_processMining, setError],
  );

  if (isLoading) {
    return <ProcessMiningLoader />;
  }

  return (
    <Box
      sx={{
        width: '600px',
        backgroundColor: 'transparent',
        boxSizing: 'border-box',
        padding: 0,
        position: 'relative',
      }}
    >
      {onClose && (
        <Box
          sx={{
            position: 'absolute',
            top: '4px',
            right: 0,
            zIndex: 2,
          }}
        >
          <IconButton variant="system" onClick={onClose} size="tiny">
            <Close />
          </IconButton>
        </Box>
      )}
      <ProcessMiningPrompt
        onSendMessage={onSendMessage}
        error={error}
        loading={isLoading}
        message={message}
      />
    </Box>
  );
};
